import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';

interface Props {
	children: ReactNode;
	/** Optional fallback — if not provided, uses the default inline error */
	fallback?: ReactNode;
}

interface State {
	hasError: boolean;
	retryKey: number;
}

/**
 * Route-level error boundary that catches render errors from child components.
 *
 * Pages don't need explicit error handling — if a query fails and the component
 * tries to access undefined data, the render throws and this boundary catches it.
 * "Try again" forces a full remount of children, which re-executes all queries.
 */
export class FeatureErrorBoundary extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = { hasError: false, retryKey: 0 };
	}

	static getDerivedStateFromError(): Partial<State> {
		return { hasError: true };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		console.error('[FeatureErrorBoundary]', error, errorInfo);
	}

	handleRetry = () => {
		this.setState((prev) => ({ hasError: false, retryKey: prev.retryKey + 1 }));
	};

	render() {
		if (this.state.hasError) {
			if (this.props.fallback) return this.props.fallback;

			return (
				<div className="flex flex-1 flex-col items-center justify-center gap-4 py-16">
					<div className="grid h-12 w-12 place-items-center rounded-full bg-destructive/10">
						<AlertCircle className="h-6 w-6 text-destructive" />
					</div>
					<div className="text-center">
						<p className="text-base font-semibold text-text-1">Something went wrong</p>
						<p className="mt-1 text-sm text-text-3">
							An error occurred while loading this section.
						</p>
					</div>
					<Button variant="outline" onClick={this.handleRetry}>
						<RefreshCw className="h-3 w-3" />
						Try again
					</Button>
				</div>
			);
		}

		return <div key={this.state.retryKey}>{this.props.children}</div>;
	}
}
